import { useEffect, useRef } from 'react';

/**
 * MarketingField
 * ------------------------------------------------------------------
 * Ambient "course topography" canvas + scroll-reveal driver for the
 * premium marketing homepage. Ported from the approved prototype's
 * inline <script>.
 *
 *  - Renders a fixed, aria-hidden <canvas id="field"> that draws a soft
 *    field of drifting topographic lines (positioned + faded by the
 *    `.mkt #field` rules in marketingRedesign.css).
 *  - Wires an IntersectionObserver that adds the `in` class to every
 *    `.mkt .reveal` element as it scrolls into view.
 *  - Fully respects `prefers-reduced-motion`: when reduced motion is
 *    requested it renders nothing and starts no effects. The CSS
 *    `prefers-reduced-motion` block already makes `.reveal` content
 *    visible without JS, so no reveal fallback is needed there.
 *
 * All effects clean up after themselves (cancelAnimationFrame, resize
 * listener removal, observer disconnect) so the component is safe to
 * mount/unmount on SPA route changes.
 */
export default function MarketingField() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const prefersReduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── scroll reveal ────────────────────────────────────────────────
  useEffect(() => {
    if (prefersReduced) return;
    const els = Array.from(
      document.querySelectorAll<HTMLElement>('.mkt .reveal'),
    );
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 },
    );
    els.forEach((el) => {
      if (!el.classList.contains('in')) io.observe(el);
    });
    return () => io.disconnect();
  }, [prefersReduced]);

  // ── ambient course-topography field ──────────────────────────────
  useEffect(() => {
    if (prefersReduced) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let dpr = 1;
    let raf = 0;
    let t = 0;

    const size = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = cv.clientWidth;
      h = cv.clientHeight;
      cv.width = w * dpr;
      cv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener('resize', size);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const lines = 16;
      for (let i = 0; i < lines; i++) {
        const yBase = h * 0.1 + (i / (lines - 1)) * (h * 0.95);
        const amp = 14 + i * 2.4;
        const op = 0.05 + (i / lines) * 0.12;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 14) {
          const y =
            yBase +
            Math.sin(x * 0.006 + t * 0.6 + i * 0.5) * amp +
            Math.sin(x * 0.013 - t * 0.4 + i) * amp * 0.4;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, 'rgba(0,113,237,0)');
        grad.addColorStop(0.5, 'rgba(0,140,255,' + op + ')');
        grad.addColorStop(1, 'rgba(0,173,237,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      t += 0.012;
      raf = window.requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
    };
  }, [prefersReduced]);

  if (prefersReduced) return null;

  return <canvas ref={canvasRef} id="field" aria-hidden="true" />;
}
